import {useCallback, useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import {ListTable} from '../common/ListTable';
import {ListPagination} from '../common/ListPagination';
import Container from '../common/layout/Container';
import Error from '../common/Error';
import {getSortedParams} from '../../helpers';
import Spinner from '../common/Spinner';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import AddDoctor from './AddDoctor';
import RequestError from '../../api/RequestError';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import {
  AddDoctorProps, DoctorListProps, DoctorResponseProps
} from '../../constants/types';
import permissionsInfo from '../../constants/permissionsInfo';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import {
  addDoctor,
  deleteDoctorById,
  getDoctors,
  updateDoctorById
} from '../../api/doctors';
import {Button} from '../common/Button';
import url from 'url';
import NoItems from '../common/no_items/NoItems';
import {noItemsMessages} from '../../constants/constants';
import {DEFAULT_ERROR} from '../../api/constants';
import Row from './Row';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const entity = {
  fetchUrl: () => '/doctors',
  header: 'Referring Doctors Configuration',
  title: 'List of Referring Doctors',
  permissions: permissionsInfo.doctors.view,
  loadingText: 'Getting referring doctors list',
  fields: [{
    name: 'id',
    label: 'ID',
    className: 'cell header-cell id-cell',
    permission: permissionsInfo.doctors.view
  },
  {
    name: 'lastName',
    label: 'Last Name',
    permission: permissionsInfo.doctors.view
  },
  {
    name: 'firstName',
    label: 'First Name',
    permission: permissionsInfo.doctors.view
  },
  {
    name: 'externalId',
    label: 'External ID',
    permission: permissionsInfo.doctors.view
  },
  {
    name: 'delete',
    label: '',
    className: 'cell header-cell narrow-cell',
    permission: permissionsInfo.doctors.delete
  }
  ]
};

export type DoctorToDeleteProps = {
  id: number,
  message: string
}

const initialQuery = {
  page: '1',
  per_page: '10'
};

type LocationState = {
  from?: string,
  callSheetsPathMemo?: string
}

export const DoctorsList = () => {
  const {t} = useTranslation('doctors');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {pathname, search, state}:
    {pathname: string, search: string, state: LocationState} = useLocation();
  const {from} = state || {from: ''};
  const {callSheetsPathMemo} = state || {callSheetsPathMemo: ''};
  const {activeTenant} = useAppSelector(({permissions}) => permissions);
  const [doctorToDelete, setDoctorToDelete] =
    useState<Nullable<DoctorToDeleteProps>>(null);
  const [doctorsData, setDoctorsData] =
    useState<Nullable<DoctorResponseProps>>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [apiValidationError, setApiValidationError] =
   useState<Nullable<Dictionary<string []>>>(null);
  const [toggleAddForm, setToggleAddForm] = useState(false);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const query = {
    ...initialQuery,
    ...getSortedParams(queryString.parse(search))
  };

  const getAllDoctors = async () => {
    setIsLoading(true);
    try {
      const response = await getDoctors(query);
      setDoctorsData(response);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (query.page !== '1' && doctorsData) {
      history.push(url.format({pathname, query: {...query, page: 1}}));
    }
  }, [activeTenant]);

  useEffect(() => {
    getAllDoctors();

    return () => {
      setError('');
    };
  }, [query?.page, query?.per_page]);

  useEffect(() => {
    history.replace(url.format({pathname, query}), {from: from});
    dispatch(setTitle(''));
  }, []);

  const {
    content = [], pageNumber = 1, totalElements = 0, totalPages = 1
  } = doctorsData || {};
  const contentLength = content?.length;

  const noItems = !totalElements && !isLoading;

  const handleAddDoctor = async (
      values: AddDoctorProps,
      resetForm: () => void
  ) => {
    try {
      const requestBody = sanitize(values);
      const newDoctor = await addDoctor(requestBody);
      addSuccessToast(t('toasts.addDoctorMessage'));

      if (from) {
        return history.push(from, {newDoctorId: newDoctor});
      }

      const shouldGoToLastPagePlusOne =
        totalElements % Number(query.per_page) === 0;
      const shouldGoToLastPage = totalPages > Number(query.page);

      if (shouldGoToLastPagePlusOne && content?.length) {
        history.push(url.format({
          pathname,
          query: {...query, page: `${totalPages + 1}`}
        }));
      } else if (shouldGoToLastPage) {
        history.push(url.format({
          pathname,
          query: {...query, page: `${totalPages}`}
        }));
      } else {
        const response = await getDoctors(query);
        setDoctorsData(response);
        resetForm();
      }
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'doctor');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const handleEditDoctor = async (id, data) => {
    const {content} = doctorsData || {};

    try {
      const currentDoctor = content
          ?.find((doctor: DoctorListProps) => doctor?.id === id);

      const {firstName, lastName, externalId} = currentDoctor || {};

      const requestBody = sanitize({
        firstName,
        lastName,
        externalId,
        ...data
      });

      await updateDoctorById(id, requestBody);
      addSuccessToast(t('toasts.updateDoctorMessage'));
      const response = await getDoctors(query);
      setDoctorsData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const handleDeleteDoctor = async () => {
    if (!doctorToDelete) return;

    setIsDeleting(true);
    const {id} = doctorToDelete;

    try {
      await deleteDoctorById(id);
      addSuccessToast(t('toasts.removeDoctorMessage'));

      const shouldGoToPreviousPage = content.length === 1 && query.page !== '1';
      if (shouldGoToPreviousPage) {
        history.push(url.format({
          pathname,
          query: {...query, page: `${Number(query?.page) - 1}`}
        }), {from: from});
      } else {
        const response = await getDoctors(query);
        setDoctorsData(response);
      }
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsDeleting(false);
      closeModal();
    }
  };

  const closeModal = useCallback(() => {
    setDoctorToDelete(null);
  }, []);

  const clearErrors = () => {
    setApiValidationError(null);
    setError('');
  };

  const handleToggleAddForm = useCallback(() => {
    setToggleAddForm((prevState) => !prevState);
  }, []);

  const InlineAddComponent = (
    <AddDoctor
      toggleAddForm={toggleAddForm}
      handleAddDoctor={handleAddDoctor}
      apiValidationError={apiValidationError}
      clearErrors={clearErrors}
    />
  );

  return (
    <>
      <Container header={entity?.header}>
        <>
          <div className="list-header">
            <h2 className="subheader">{entity?.title}</h2>
          </div>

          {isLoading && !doctorsData ?
            <Spinner loadingText={entity?.loadingText} /> :
            <Spinner loadingText={entity?.loadingText} isLoading={isLoading}>
              <>
                <ListTable
                  rowRenderer={(content) => content.map((doctorData) => (
                    <Row
                      key={`${doctorData.id}`}
                      doctor={doctorData}
                      editHandler={handleEditDoctor}
                      handleToggleAddForm={handleToggleAddForm}
                      setDoctorToDelete={setDoctorToDelete}
                    />
                  ))}
                  entity={entity}
                  content={content}
                  inlineAdd={InlineAddComponent}
                  query={query}
                />

                {noItems ?
                  (<NoItems message={noItemsMessages.ITEMS}/>) :
                  (<ListPagination
                    contentLength={contentLength}
                    pageNumber={pageNumber}
                    totalElements={totalElements}
                    totalPages={totalPages}
                    query={query}
                  />)}
              </>
            </Spinner>}

          {error && <Error message={error} />}
          {from && <Button
            label="Back"
            variant="outlined"
            color="secondary"
            onClick={() => {
              history.push({
                pathname: from,
                state: {callSheetsPathMemo}
              });
            }} />}
        </>
      </Container>

      <ConfirmationPopup
        labels={{body: doctorToDelete?.message || ''}}
        isOpen={!!doctorToDelete}
        setIsOpen={closeModal}
        confirmableAction={handleDeleteDoctor}
        isActionProcessing={isDeleting}
      />
    </>
  );
};

export default DoctorsList;
